import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { includes, length, path, pathOr } from 'ramda';
import { string, func, shape, object } from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';
import classNames from 'classnames';

import { propTypes } from '../../util/types';
import { ensureListing } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { DAYS_CONSIDERED_NEW_LISTING, getDaysSinceCreation } from '../../util/listingHelpers';
import defaultListingImg from '../../assets/default-img-listing/default-img-listing.jpg';
import defaultListingImgCrop from '../../assets/default-img-listing/landscape-crop.webp';
import defaultListingImgCrop2 from '../../assets/default-img-listing/landscape-cropx2.webp';
import { InstantBookingBadge, ResponsiveImage, SuperhostBadge } from '../../components';

import GeolocationInfo from './GeolocationInfo/GeolocationInfo';
import ReviewsInfo from './ReviewsInfo/ReviewsInfo';
import css from './ListingCard.css';
import { formatMoney } from '../../util/currency';
import SectionExtras from './SectionExtras/SectionExtras';
import SwimPlusDiscountBadge from '../SwimPlusDiscountBadge/SwimPlusDiscountBadge';
import { IconTrash } from '../Icons';

export const CartListingCardComponent = (props) => {
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    searchParams,
    setActiveListing,
    myFavorites,
    handleOpenDetails,
    selectedExtras,
    swimPlusLevel,
    isPurchasingSwimPlus,
    small,
    showDelete,
    onDelete,
  } = props;
  const currentListing = ensureListing(listing);
  const listingTitle = pathOr('', ['attributes', 'title'], currentListing);
  const createdAt = path(['attributes', 'createdAt'], currentListing);
  const paidExtrasData = pathOr([], ['attributes', 'publicData', 'paidExtrasData'], listing);
  const isHostedBySuperHost = path(['attributes', 'publicData', 'isHostedBySuperHost'], listing);
  const instantBookingFrom = path(
    ['attributes', 'publicData', 'instantBookingFrom'],
    currentListing
  );
  const firstImage = path(['images', [0]], currentListing);
  const reviewsInfo = pathOr({}, ['attributes', 'publicData', 'reviewsInfo'], currentListing);
  const isNewListing = getDaysSinceCreation(createdAt) <= DAYS_CONSIDERED_NEW_LISTING;
  const showReviewsInfo = isNewListing || reviewsInfo.totalReviews >= 1;
  const defaultListingImgVariants = `${defaultListingImgCrop} 400w, ${defaultListingImgCrop2} 800w`;
  const classes = classNames(rootClassName || css.root, className, small && css.noBoxShadow);
  const price = pathOr({}, ['estimatedTransaction', 'attributes', 'payinTotal'], listing);
  const hasExtras = length(paidExtrasData) > 0;

  return (
    <div className={classes} onClick={handleOpenDetails}>
      {/*<NamedLink to={to} name="ListingPage" params={{ id: listingUuid, slug }}>*/}
      <div
        className={small ? css.threeToTwoWrapperSmall : css.threeToTwoWrapper}
        onMouseEnter={() => setActiveListing(currentListing.id)}
        onMouseLeave={() => setActiveListing(null)}
      >
        <div className={css.aspectWrapper}>
          {!firstImage ? (
            <img
              src={defaultListingImg}
              alt={listingTitle}
              className={small ? css.rootForImageSmall : css.rootForImage}
              srcSet={defaultListingImgVariants}
            />
          ) : (
            <ResponsiveImage
              rootClassName={small ? css.rootForImageSmall : css.rootForImage}
              alt={listingTitle}
              image={firstImage}
              variants={['landscape-crop', 'landscape-crop2x']}
              sizes={renderSizes}
            />
          )}
        </div>
      </div>
      {/*</NamedLink>*/}
      {/*<NamedLink*/}
      {/*  className={css.link}*/}
      {/*  to={to}*/}
      {/*  name="ListingPage"*/}
      {/*  params={{ id: listingUuid, slug }}*/}
      {/*>*/}
      <div className={classNames(css.info, small && css.infoInline)}>
        <div className={css.titleAndReviewWrapper}>
          <span className={css.title}>{listingTitle}</span>
          {showReviewsInfo && (
            <ReviewsInfo
              className={css.reviewsInfo}
              intl={intl}
              isNewListing={isNewListing}
              reviewsInfo={reviewsInfo}
            />
          )}
        </div>
        {!small && (
          <GeolocationInfo
            className={css.geolocationInfo}
            intl={intl}
            listing={currentListing}
            searchParams={searchParams}
          />
        )}
        {swimPlusLevel > 0 && (
          <div className={css.swimPlusDiscountBadge}>
            <SwimPlusDiscountBadge
              className={css.instantBookingBadge}
              textOnly
              isLevel1={swimPlusLevel === 1}
            />
          </div>
        )}
        {!small && <div className={css.price}>{formatMoney(intl, price, true)}</div>}
        {!small && hasExtras && (
          <SectionExtras
            className={css.sectionRulesMaybe}
            titleClassName={css.sectionTitle}
            intl={intl}
            paidExtrasData={paidExtrasData}
            selectedExtras={selectedExtras}
          />
        )}
        {/*<PriceBreakdown*/}
        {/*  className={css.priceBreakdown}*/}
        {/*  transaction={listing.estimatedTransaction}*/}
        {/*  userRole={CUSTOMER}*/}
        {/*/>*/}
      </div>
      {small && <div className={css.priceInline}>{formatMoney(intl, price, true)}</div>}
      <div className={css.badgeContainer}>
        {instantBookingFrom && <InstantBookingBadge className={css.instantBookingBadge} textOnly />}
      </div>
      {showDelete && (
        <button
          type="button"
          className={css.deleteButton}
          onClick={(e) => {
            e.stopPropagation();
            if (onDelete) {
              onDelete();
            }
          }}
        >
          <IconTrash className={css.deleteIcon} />
        </button>
      )}
      {/*</NamedLink>*/}
    </div>
  );
};

CartListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  search: null,
  searchParams: {},
  to: null,
  setActiveListing: () => null,
};

CartListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  search: object,
  searchParams: object,
  to: shape({ search: string }),

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

const mapStateToProps = (state) => {
  const { currentUser } = state.user;
  const { isAuthenticated } = state.Auth;
  const myFavorites = pathOr(
    [],
    ['attributes', 'profile', 'protectedData', 'myFavorites'],
    currentUser
  );

  return {
    myFavorites,
    isAuthenticated,
  };
};

const CartListingCard = compose(
  connect(mapStateToProps, null),
  injectIntl
)(CartListingCardComponent);

export default CartListingCard;
